<script setup lang="ts">
import { watch, watchEffect, useAttrs, ref } from 'vue';
import ConnxTransition from '@/components/Atoms/ConnxTransition.vue';
import ConnxIcon from '../Atoms/ConnxIcon.vue';
import { useEventListener } from '@vueuse/core';
import { useScroll } from '@vueuse/core';

type Size = 'small' | 'medium' | 'large';

interface Props {
  open: boolean;
  type?: 'default' | 'side';
  size?: Size;
  title?: string;
  scrollable?: boolean;
  zIndex?: string;
  showClose?: boolean;
}

const slots = defineSlots<{
  default(): any;
  footer?: any;
}>();
const attrs = useAttrs();
const modalRef = ref();

const sectionContentRef = ref<HTMLElement | null>(null);
const { y: sectionContentY } = useScroll(sectionContentRef);

const props = withDefaults(defineProps<Props>(), {
  type: 'default',
  size: 'small',
  zIndex: 'tw-z-1100',
});

const emit = defineEmits(['close']);

watchEffect((onCleanup) => {
  if (!props.open) return;

  const stop = useEventListener(modalRef, 'keyup', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      emit('close');
    }
  });

  onCleanup(() => stop());
});

const scrollToTop = () => {
  sectionContentY.value = 0;
};

const focus = () => {
  if (modalRef.value?.focus === 'function') {
    modalRef.value?.focus();
  }
};

defineExpose({
  scrollToTop,
  focus,
});
</script>

<template>
  <Teleport to="body">
    <div class="tailwindcss">
      <div
        v-show="open"
        ref="modalRef"
        class="tw-fixed tw-left-0 tw-top-0 tw-right-0 tw-bottom-0 tw-bg-connx-black-rgb/20 tw-flex"
        :class="[
          zIndex,
          {
            'tw-justify-center tw-items-center': type === 'default',
            'tw-justify-end': type === 'side',
          },
        ]"
      >
        <connx-transition>
          <div
            v-show="open"
            :class="{
              'tw-max-w-2xl': size === 'small',
              'tw-max-w-4xl': size === 'medium',
              'tw-max-w-7xl': size === 'large',
              'tw-py-10': type === 'default',
            }"
            class="tw-w-full tw-flex tw-flex-col"
          >
            <div
              class="tw-bg-connx-white tw-mx-auto tw-border tw-border-solid tw-flex-1 tw-w-full tw-shadow-sm tw-flex"
              :class="{
                'tw-rounded-md': type === 'default',
              }"
            >
              <div class="tw-grow tw-flex tw-flex-col tw-justify-between">
                <header
                  v-if="title"
                  class="tw-border-solid tw-border-b tw-border-connx-grey-tint-80 tw-p-8 tw-flex tw-items-center tw-justify-between"
                >
                  <h1 class="tw-text-2xl tw-font-semibold tw-text-connx-secondary-header tw-mb-0">
                    {{ title }}
                  </h1>
                  <button
                    type="button"
                    class="tw-border-none tw-text-connx-grey-tint-20 hover:tw-text-connx-grey-shade-20"
                    @click="emit('close')"
                  >
                    <connx-icon name="fal fa-times" class="tw-w-3" />
                  </button>
                </header>
                <header v-else-if="showClose" class="tw-flex tw-items-center tw-justify-end tw-pt-8 tw-px-8">
                  <button
                    type="button"
                    class="tw-border-none tw-text-connx-grey-tint-20 hover:tw-text-connx-grey-shade-20"
                    @click="emit('close')"
                  >
                    <connx-icon name="fal fa-times" class="tw-w-3" />
                  </button>
                </header>
                <main
                  :class="{
                    'tw-max-w-2xl': size === 'small',
                    'tw-max-w-4xl': size === 'medium',
                    'tw-max-w-7xl': size === 'large',
                  }"
                  class="tw-mx-auto tw-w-full tw-h-full tw-flex tw-flex-col tw-justify-start"
                >
                  <div
                    ref="sectionContentRef"
                    :class="
                      (attrs.class,
                      {
                        ScrollArea: scrollable && type === 'default',
                        ScrollAreaSideModal: scrollable && type === 'side',
                        'tw-py-4': !slots.footer && scrollable,
                        'tw-pt-4': slots.footer && scrollable,
                        'tw-py-10 tw-flex tw-items-center tw-justify-center': !scrollable,
                      })
                    "
                    class="tw-px-8"
                  >
                    <section class="tw-w-full">
                      <slot></slot>
                    </section>
                  </div>
                </main>
                <footer
                  v-if="slots.footer"
                  class="tw-rounded-b-lg tw-w-full tw-bg-connx-grey-tint-85 tw-mx-auto tw-border-solid tw-border-t tw-border-connx-grey-tint-80 tw-py-6"
                >
                  <div class="tw-px-8">
                    <div>
                      <slot name="footer"></slot>
                    </div>
                  </div>
                </footer>
              </div>
            </div>
          </div>
        </connx-transition>
      </div>
    </div>
  </Teleport>
</template>
